// contexts/WebSocketContext.jsx
import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
} from "react";

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error("useWebSocket debe ser usado dentro de WebSocketProvider");
    }
    return context;
};

export const WebSocketProvider = ({ children, url }) => {
    const [isConnected, setIsConnected] = useState(false);
    let op = require("../modulos/datos");
    const ws = useRef(null);
    const pingIntervalRef = useRef(null); // Guardar referencia del intervalo
    const reconnectTimeoutRef = useRef(null); // Guardar referencia del timeout de reconexión
    const joinTimeoutRef = useRef(null); // Guardar referencia del timeout de join

    // Función para obtener el usuario de manera segura
    const getUserFromStorage = useCallback(() => {
        try {
            const postData = localStorage.getItem('post');
            if (!postData) {
                return null;
            }

            const obj = JSON.parse(postData);
            if (!obj || !obj.login) {
                return null;
            }

            return obj.login;
        } catch (error) {
            return null;
        }
    }, []);

    const connect = useCallback(() => {
        try {
            // Limpiar timeouts existentes
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (joinTimeoutRef.current) {
                clearTimeout(joinTimeoutRef.current);
            }

            ws.current = new WebSocket(url);

            ws.current.onopen = () => {
                setIsConnected(true);
                console.log("WebSocket conectado");
                // Obtener el usuario de manera segura
                const username = getUserFromStorage();

                // Enviar mensaje de join después de 2 segundos
                joinTimeoutRef.current = setTimeout(() => {
                    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                        if (username) {
                            const joinMessage = {
                                type: "join_room",
                                data: {
                                    room: op.nombre,
                                    username: username
                                },
                            };
                            ws.current.send(JSON.stringify(joinMessage));
                        } else {
                            console.warn("No se pudo obtener el usuario");
                        }
                    }
                }, 2000);
            };

            ws.current.onclose = (event) => {
                setIsConnected(false);

                // Limpiar intervalo de ping al desconectar
                if (pingIntervalRef.current) {
                    clearInterval(pingIntervalRef.current);
                    pingIntervalRef.current = null;
                }

                // Reconexión automática después de 10 segundos
                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, 10000);
            };

            ws.current.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);

                } catch (error) {
                    console.error("Error al parsear mensaje:", error);
                }
            };

            ws.current.onerror = (error) => {
                console.error("WebSocket error:", error);
            };

            // Configurar ping periódico solo cuando se conecta
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
            }

            pingIntervalRef.current = setInterval(() => {
                if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                    ws.current.send(JSON.stringify({ type: "ping" }));
                }
            }, 30000);

        } catch (error) {
            console.error("Error al conectar WebSocket:", error);
        }
    }, [url, getUserFromStorage]);

    const sendMessage = useCallback((message) => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            try {
                ws.current.send(JSON.stringify(message));
                console.log("📤 Mensaje enviado:", message);
            } catch (error) {
                console.error("Error al enviar mensaje:", error);
            }
        } else {
            console.warn("WebSocket no está conectado. Estado:", ws.current?.readyState);
        }
    }, []);

    const sendPrint = useCallback((data) => {
        console.log("Enviando impresión:", data);

        if (!data) {
            console.error("No se proporcionaron datos para imprimir");
            return;
        }

        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            try {
                const printMessage = {
                    type: "send_print",
                    data: {
                        message: data.message || "",
                        server: data.server || "",
                        impresora: data.impresora || "",
                        idfactura: data.idfactura || "",
                        fecha: data.fecha || new Date().toISOString(),
                        tipo: data.tipo || "",
                        tipopago: data.tipopago || "",
                        copias: data.copias || 1,
                        papel: data.papel || "thermal",
                    },
                };

                ws.current.send(JSON.stringify(printMessage));
                console.log("📤 Impresión enviada:", printMessage);
            } catch (error) {
                console.error("Error al enviar impresión:", error);
            }
        } else {
            console.warn("WebSocket no está conectado para enviar impresión");
        }
    }, []);

    const disconnect = useCallback(() => {
        // Limpiar todos los intervalos y timeouts
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        if (joinTimeoutRef.current) {
            clearTimeout(joinTimeoutRef.current);
            joinTimeoutRef.current = null;
        }

        if (ws.current) {
            ws.current.close();
            ws.current = null;
        }
    }, []);

    const reconnect = useCallback(() => {
        disconnect();
        setTimeout(() => {
            connect();
        }, 1000);
    }, [connect, disconnect]);

    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    const value = {
        isConnected,
        sendMessage,
        sendPrint,
        disconnect,
        reconnect,
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
};